import React, { useState, useEffect, useRef } from "react";
import "../../styles/header/header.css";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import CustomizeIcon from "../../assets/img/healthicons--ui-preferences.svg";
import LogoutIcon from "../../assets/img/hugeicons--logout-03.svg";
import ConfirmIcon from "../../assets/img/fluent--people-checkmark-16-filled.svg";
import { useTheme } from "../../ThemeContext";
import Modal from "../modal/Modal";
import PrimaryButton from "../buttons/PrimaryButton";
import axios from "axios";
import '../../styles/modal/modal.css';

const Header = () => {
  const navigate = useNavigate();
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [is2FAEnabled, setIs2FAEnabled] = useState(false);
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [show2FADisableModal, setShow2FADisableModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [qrCode, setQrCode] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [error, setError] = useState("");
  const { theme, toggleTheme } = useTheme();
  const dropdownRef = useRef(null);
  const baseUrl = process.env.REACT_APP_BASE_URL;

  useEffect(() => {
    const twoFAEnabled = Cookies.get("twoFAEnabled");
    setIs2FAEnabled(twoFAEnabled === "true");
  }, []);

  const handleLogOut = () => {
    Cookies.remove("token");
    Cookies.remove("role");
    Cookies.remove("userName");
    Cookies.remove("tokenTimestamp");
    navigate("/");
  };

  const handleShowDropdown = () => {
    setIsDropdownVisible(!isDropdownVisible);
  };

  const token = Cookies.get("token");
  const handleEnable2FA = async () => {
    try {
      setError("");
      const response = await axios.post(
        `${baseUrl}/api/users/setup-2fa`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        setQrCode(response.data.qrCode);
        setShow2FAModal(true);
      }
    } catch (error) {
      setError("Failed to setup 2FA. Please try again.");
    }
  };

  const handleVerify2FA = async () => {
    try {
      setError("");
      const response = await axios.post(
        `${baseUrl}/api/users/enable-2fa`,
        {
          code: verificationCode,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        setIs2FAEnabled(true);
        Cookies.set("twoFAEnabled", "true");
        setShow2FAModal(false);
        setVerificationCode("");
        setQrCode("");
        setConfirmationMessage("Two-factor authentication has been successfully enabled!");
        setShowConfirmationModal(true);
      }
    } catch (error) {
      setError(
          "Failed to verify 2FA code. Please try again."
      );
    }
  };

  const handleDisable2FA = async () => {
    try {
      setError("");
      const response = await axios.post(
        `${baseUrl}/api/users/disable-2fa`,
        {
          code: verificationCode,
        },
        {
          headers: {
            Authorization: `Bearer ${Cookies.get("token")}`,
          },
        }
      );

      if (response.status === 200) {
        setIs2FAEnabled(false);
        Cookies.set("twoFAEnabled", "false");
        setVerificationCode("");
        setShow2FADisableModal(false);
        setConfirmationMessage("Two-factor authentication has been successfully disabled!");
        setShowConfirmationModal(true);
      }
    } catch (error) {
      setError(
          "Failed to disable 2FA. Please try again."
      );
    }
  };

  const handleCloseConfirmationModal = () => {
    setShowConfirmationModal(false);
    setConfirmationMessage("");
  };

  useEffect(() => {
    let timeoutId;
    if (showConfirmationModal) {
      timeoutId = setTimeout(() => {
        handleCloseConfirmationModal();
      }, 3000);
    }
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [showConfirmationModal]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownVisible(false);
      }
    };

    if (isDropdownVisible) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownVisible]);

  return (
    <>
      <div className="header-container">
        <div className="logo-name">MonitorPro</div>
        <div className="icons-container">
          <div
            className="icon settings"
            onClick={handleShowDropdown}
            ref={dropdownRef}
            style={{ cursor: "pointer" }}
          >
            <div className="settings-container">
              <Icon icon="lets-icons:setting-alt-line" width="24" height="24" />
            </div>

            {isDropdownVisible && (
              <div className="dropdown-menu">
                <button className="dropdown-button" onClick={toggleTheme}>
                  <img
                    src={CustomizeIcon}
                    alt="Customize"
                    width="24"
                    height="24"
                    className="dropdown"
                  />
                  <span className="theme-span">
                    {theme === "light" ? "Dark Mode" : "Light Mode"}
                  </span>
                </button>
                <button
                  className="dropdown-button"
                  onClick={() => {
                    if (is2FAEnabled) {
                      setShow2FADisableModal(true);
                    } else {
                      handleEnable2FA();
                    }
                  }}
                >
                  <Icon
                    icon="mdi:shield-lock"
                    width="24"
                    height="24"
                    className="dropdown"
                    color="#078ECE"
                  />
                  <span>{is2FAEnabled ? "Disable 2FA" : "Enable 2FA"}</span>
                </button>
              </div>
            )}
          </div>
          <button
            className="icon"
            onClick={handleLogOut}
            style={{ cursor: "pointer" }}
          >
            <Icon icon="mdi:logout" width="24" height="24" />
            <span>Logout</span>
          </button>
        </div>
      </div>

      {/* 2FA Setup Modal */}
      {show2FAModal && (
        <Modal show={show2FAModal} onClose={() => setShow2FAModal(false)}>
          <div className="title">SETUP TWO-FACTOR AUTHENTICATION </div>
          <p>
            Scan this QR code with Google Authenticator App
          </p>
          {qrCode && (
            <img
              src={qrCode}
              alt="2FA QR Code"
              style={{ width: "200px", height: "200px" }}
            />
          )}
          <div className="verification-input">
            <input
              type="text"
              placeholder="Enter verification code"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
            />
            <PrimaryButton onClick={handleVerify2FA} text="Verify"/>
          </div>
          {error && <p className="error-message">{error}</p>}
        </Modal>
      )}

      {/* 2FA Disable Modal */}
      {show2FADisableModal && (
        <Modal show={show2FADisableModal} onClose={() => setShow2FADisableModal(false)}>
          <div className="title">DISABLE TWO-FACTOR AUTHENTICATION</div>
          <p>Please enter your verification code to disable 2FA</p>
          <div className="verification-input">
            <input
              type="text"
              placeholder="Enter verification code"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
            />
            <PrimaryButton onClick={handleDisable2FA} text="Disable" />
          </div>
          {error && <p className="error-message">{error}</p>}
        </Modal>
      )}

      {/* Confirmation Modal */}
      {showConfirmationModal && (
        <Modal
          show={showConfirmationModal}
          onClose={handleCloseConfirmationModal}
        >
          <div className="content confirmation">
            <div className="icon-container">
              <img
                src={ConfirmIcon}
                alt="confirm"
                width="57"
                height="57"
                style={{ color: "#078ECE" }}
              />
            </div>
            <div className="text">
              {confirmationMessage}
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default Header;
